<template>
    <ModalDialog ref="modalDialog" @close="closed">
        <template v-slot:title>
            {{ isEdit ? "Edit Contact Detail" : "New Contact Detail" }}
        </template>
        <template v-slot:body>
            <div
                v-if="isContactAlreadyAdded"
                id="dialog-alert-warning"
                class="alert alert-block alert-warning padding-alert"
            >
                <span id="dialog-alert-message">
                    <i class="cus-error"></i> This contact cannot be added as it
                    already exists.
                </span>
            </div>
            <div class="form-group form-row">
                <label for="type" class="col-md-4 col-form-label">Type:</label>
                <div class="col-md-8">
                    <select
                        v-model="form.type"
                        name="type"
                        id="type"
                        class="form-control"
                        :disabled="isEdit"
                        @change="onChangeType"
                    >
                        <option
                            :value="type.ID"
                            v-for="type in contactTypeOptions"
                            :key="type.ID"
                            >{{ type.LABEL }}</option
                        >
                    </select>
                </div>
            </div>

            <div class="form-group form-row">
                <label for="type" class="col-md-4 col-form-label"
                    >Contact:</label
                >
                <div class="col-md-8" v-if="isPhoneType || isFaxType">
                    <div class="form-row">
                        <div class="col-4">
                            <CountryCode
                                v-model="form.country_code"
                                name="country_code"
                                id="country-code"
                                :defaultValue="$root.setting.phone_code"
                                class="form-control"
                            />
                        </div>
                        <div class="col-8">
                            <input
                                id="contact"
                                v-model="form.contact"
                                v-validate="'phone_number|required'"
                                :class="{
                                    'border-danger': errors.has('contact')
                                }"
                                name="contact"
                                type="text"
                                class="form-control"
                                :placeholder="typePlaceholder"
                            />
                        </div>
                    </div>
                </div>
                <div class="col-md-8" v-else-if="isEmailType">
                    <input
                        id="contact"
                        v-model="form.contact"
                        :class="{
                            'border-danger': errors.has('contact')
                        }"
                        v-validate="'email|required'"
                        type="contact"
                        class="form-control"
                        name="contact"
                        :placeholder="typePlaceholder"
                    />
                </div>
            </div>

            <div class="form-group form-row" v-if="!isEdit">
                <label for="is-primary" class="col-md-4 col-form-label"
                    >Primary:</label
                >
                <div class="col-md-8">
                    <select
                        v-model="form.is_primary"
                        name="is_primary"
                        id="is-primary"
                        class="form-control"
                        :disabled="!selectedTypeHasPrimary"
                        @change="onChangePrimary"
                    >
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </div>
            </div>

            <div class="form-group form-row" v-if="isTypeSupportForReminder">
                <label for="is-primary" class="col-md-4 col-form-label"
                    >Receive Automated Reminders:</label
                >
                <div class="col-md-8">
                    <select
                        v-model="form.receive_automated_reminder"
                        name="is_primary"
                        id="is-primary"
                        class="form-control"
                        :disabled="!isAbleChangeReceiveAutomatedReminder"
                    >
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </div>
            </div>

            <div class="form-group form-row">
                <label for="description" class="col-md-4 col-form-label"
                    >Description:</label
                >
                <div class="col-md-8">
                    <textarea
                        v-model="form.description"
                        name="description"
                        class="form-control"
                        rows="3"
                        placeholder="Description (optional)"
                    ></textarea>
                </div>
            </div>
        </template>
        <template v-slot:confirmBtn>
            <div class="ml-auto">
                <div v-if="!isSaving">
                    <button
                        v-if="isEdit"
                        :disabled="isLoading || isContactAlreadyAdded"
                        class="btn btn-primary"
                        @click="save(false)"
                    >
                        Save
                    </button>
                    <b-button-group v-else>
                        <b-dropdown
                            left
                            split
                            :text="isAddNew ? 'Save + Add New' : 'Save + Done'"
                            variant="primary"
                            :disabled="isLoading || isContactAlreadyAdded"
                            @click="save(isAddNew)"
                        >
                            <b-dropdown-item @click="save(!isAddNew)">
                                {{
                                    isAddNew ? "Save + Done" : "Save + Add New"
                                }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>
                </div>
                <loader v-else :margin="1"></loader>
            </div>
        </template>
    </ModalDialog>
</template>

<script>
import ModalDialog from "@/components/commons/modal-dialog";
import CountryCode from "@/module/commons/countrycode.vue";
import ContactDetailService from "@/services/client-contact-details";
import CLIENT from "@/constants/clients";
import Loader from "@/module/commons/loader";
import { isEmpty } from "lodash";

const initialForm = () => ({
    id: null,
    type: CLIENT.CONTACT_DETAIL_TYPES.EMAIL,
    contact: "",
    country_code: null,
    is_primary: false,
    receive_automated_reminder: false,
    description: ""
});

export default {
    components: {
        ModalDialog,
        Loader,
        CountryCode
    },
    data() {
        return {
            contacts: [],
            isLoading: false,
            isAddNew: false,
            form: initialForm(),
            isSaving: false,
            isOnceSavePrimary: false
        };
    },
    computed: {
        clientId() {
            return this.$store.state.client.client_id;
        },
        contactTypeOptions() {
            return Object.values(CLIENT.CONTACT_DETAIL_TYPE_LIST).filter(
                type => type.ID !== CLIENT.CONTACT_DETAIL_TYPES.ALL
            );
        },
        isPhoneType() {
            return [
                CLIENT.CONTACT_DETAIL_TYPES.MOBILE,
                CLIENT.CONTACT_DETAIL_TYPES.HOME,
                CLIENT.CONTACT_DETAIL_TYPES.WORK
            ].includes(this.form.type);
        },
        isMobileType() {
            return CLIENT.CONTACT_DETAIL_TYPES.MOBILE == this.form.type;
        },
        isFaxType() {
            return CLIENT.CONTACT_DETAIL_TYPES.FAX == this.form.type;
        },
        isEmailType() {
            return CLIENT.CONTACT_DETAIL_TYPES.EMAIL == this.form.type;
        },
        typePlaceholder() {
            if (this.isPhoneType) {
                return `${
                    CLIENT.CONTACT_DETAIL_TYPE_LIST[this.form.type].LABEL
                } Phone (required)`;
            } else if (this.isFaxType) {
                return "Fax (required)";
            } else if (this.isEmailType) {
                return "Email (required)";
            }

            return "";
        },
        isEdit() {
            return !isEmpty(this.form.id);
        },
        selectedTypeHasPrimary() {
            return this.contacts.some(contact => {
                return contact.type == this.form.type && contact.is_primary;
            });
        },
        isContactAlreadyAdded() {
            if (!this.contacts.length || this.form.contact === "") {
                return false;
            }

            if (
                this.contacts.some(
                    contact =>
                        contact.id != this.form.id &&
                        contact.type == this.form.type &&
                        (this.isFaxType || this.isPhoneType
                            ? contact.contact == this.form.contact &&
                              contact.country_code == this.form.country_code
                            : contact.contact == this.form.contact)
                )
            ) {
                return true;
            }

            return false;
        },
        selectedReminderOptions() {
            return this.$store.state.client.client_details.reminderOptions;
        },
        isTypeSupportForReminder() {
            if (isEmpty(this.selectedReminderOptions)) {
                return false;
            }

            if (this.isEmailType) {
                return this.selectedReminderOptions.includes(
                    String(this.REMINDER_TYPE.EMAIL)
                );
            } else if (this.isMobileType) {
                return this.selectedReminderOptions.includes(
                    String(this.REMINDER_TYPE.SMS)
                );
            }

            return false;
        },
        isAbleChangeReceiveAutomatedReminder() {
            return (
                this.isTypeSupportForReminder &&
                this.selectedTypeHasPrimary &&
                !this.form.is_primary
            );
        }
    },
    mounted() {
        this.$validator.pause();
    },
    methods: {
        onChangeType() {
            if (this.form.id) {
                return;
            }

            this.form.is_primary = this.selectedTypeHasPrimary ? false : true;
            this.setReceiveAutomatedReminder();
        },
        onChangePrimary() {
            this.setReceiveAutomatedReminder();
        },
        setReceiveAutomatedReminder() {
            if (this.form.is_primary && this.isTypeSupportForReminder) {
                this.form.receive_automated_reminder = true;
            } else {
                this.form.receive_automated_reminder = false;
            }
        },
        async getContacts() {
            this.isLoading = true;

            try {
                const {
                    response
                } = await ContactDetailService.getContactDetails(this.clientId);
                if (response.code == 200) {
                    this.contacts = response.resources;

                    if (!this.selectedTypeHasPrimary) {
                        this.form.is_primary = true;
                    }
                } else {
                    throw new Error(response.message);
                }
            } catch (error) {
                console.error(error.message);
                this._message(
                    "Error : Unable to get contact details",
                    "danger"
                );
            } finally {
                this.isLoading = false;
            }
        },
        async open(formData = {}) {
            this.$refs.modalDialog.open();

            if (formData.id) {
                this.form.id = formData.id;
                this.form.type = formData.type;
                this.form.contact = formData.contact;
                this.form.country_code = formData.country_code;
                this.form.is_primary = formData.is_primary === 1;
                this.form.receive_automated_reminder =
                    formData.receive_automated_reminder === 1;
                this.form.description = formData.description;
            }

            await this.getContacts();

            if (this.$validator._paused) {
                this.$validator.resume();
            }
        },
        reset() {
            this.$validator.reset();
            this.$validator.pause();

            this.form = Object.assign({}, initialForm());
        },
        async closed() {
            this.reset();
            this.isAddNew = false;
        },
        close() {
            this.closed();
            this.$refs.modalDialog.close();
            this.$emit("close");
        },
        async resetToNewForm() {
            this.reset();

            await this.getContacts();
            this.$validator.resume();
        },
        async save(isAddNew = false) {
            let isValid = await this.$validator.validate();

            if (!isValid) {
                return;
            }

            try {
                this.isSaving = true;

                const payload = {
                    id: this.form.id,
                    contact: this.form.contact,
                    country_code: this.form.country_code,
                    type: this.form.type,
                    is_primary: this.form.is_primary,
                    receive_automated_reminder: this.isTypeSupportForReminder
                        ? this.form.receive_automated_reminder
                        : false,
                    description: this.form.description
                };

                const { response } = this.isEdit
                    ? await ContactDetailService.updateContactDetail(
                          this.clientId,
                          payload
                      )
                    : await ContactDetailService.addContactDetail(
                          this.clientId,
                          payload
                      );

                if (response.code == 200) {
                    this._message(
                        this.isEdit
                            ? "Contact detail successfully saved"
                            : "Contact detail successfully added"
                    );

                    this.isAddNew = isAddNew;

                    this.$emit("onsuccess", this.form.is_primary);

                    if (this.isAddNew) {
                        this.resetToNewForm();
                    } else {
                        this.close();
                    }
                } else {
                    throw new Error(response.message);
                }
            } catch (error) {
                console.error(error.message);
                this._message("Error saving data", "danger");
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>

<style scoped>
.padding-alert {
    padding: 10px;
}
</style>
